import {
  CheckBoxGroup,
  Dropdown,
  FormLayout,
  ImgUploadButton,
  MultiSelectDropdown,
  TextInput,
} from 'components/common'
import SwitchField from 'components/bundleProduct/switchField'
import { Control } from 'react-hook-form'
import {
  aggregatorFormFields,
  CommonFormType,
  getNestErrorMessage,
} from '../utils'
import { useEffect, useState } from 'react'

type MobileAppConfiguratinFormProps = {
  isEditPage?: boolean
  watch?: any
  control: Control<any>
} & CommonFormType

const {
  BARCODE_REGEX,
  ORDER_TYPES,
  DELIVERY_FEE_AMOUNT,
  REQUIRE_ADDRESS,
  REQUIRE_CUSTOMER_NAME,
  REQUIRE_PHONE,
  KEYBOARD_CONFIGURATION,
  ORDER_NUM_LENGTH,
  IS_MANUAL_ENTRY_ENABLE,
  IS_DELIVERY_FEE_ENABLE,
  IS_DELIVERY_FEE_MANDATORY,
  POS_PAYMENTS_MODE,
  CUSTOMER_ID,
  ENABLE_PICKER_ASSIGNMENT,
  ALLOW_ITEM_REPLACEMENT,
} = aggregatorFormFields

const MobileAppConfigurationForm = ({
  setValue,
  register,
  errors,
  //   isEditPage = false,
  aggregator,
  watch,
  control,
}: MobileAppConfiguratinFormProps) => {
  // Order Types Options
  const [orderTypesOptions, setOrderTypesOptions] = useState([
    {
      label: '--NONE--',
      value: '',
      disabled: true,
    },
  ])

  // const [paymentOptions, setPaymentOptions] = useState([
  //   {
  //     label: '--NONE--',
  //     value: '',
  //     disabled: true,
  //   },
  // ])

  const handleFetchOrderTypes = async () => {
    if (aggregator?.mobile?.allOrderTypes?.length) {
      setOrderTypesOptions([
        ...orderTypesOptions,
        ...aggregator.mobile.allOrderTypes,
      ])
    }
  }

  const paymentOptions = aggregator?.mobile?.allPayments.map((option: any) => ({
    label: option.label,
    value: option.value,
  }))

  useEffect(() => {
    handleFetchOrderTypes()
  }, [])

  useEffect(() => {
    const recon = aggregator?.mobile?.reconciliationPaymentTypes

    if (!recon) return

    Object.entries(recon).forEach(([key, value]) => {
      setValue(`mobile.reconciliationPaymentTypes.${key}`, value)
    })
  }, [aggregator?.mobile?.reconciliationPaymentTypes, setValue])

  const isDeliveryFeeEnabled = watch(
    IS_DELIVERY_FEE_ENABLE.parentKey + '.' + IS_DELIVERY_FEE_ENABLE.key,
  )

  return (
    <>
      <h3 className="title mb-1">Mobile App Configuration</h3>

      <FormLayout>
        <div className="flex flex-col gap-6 lg:flex-row lg:items-start lg:gap-8">
          {/* General settings */}
          <div className="flex w-full flex-col gap-3 lg:w-1/3">
            <div className="flex items-start gap-4">
              <div className="flex flex-grow flex-col gap-3">
                <TextInput
                  register={register}
                  label={BARCODE_REGEX.label}
                  name={BARCODE_REGEX.parentKey + '.' + BARCODE_REGEX.key}
                  error={getNestErrorMessage(errors, BARCODE_REGEX)}
                />

                <TextInput
                  register={register}
                  label={CUSTOMER_ID.label}
                  name={CUSTOMER_ID.parentKey + '.' + CUSTOMER_ID.key}
                  error={getNestErrorMessage(errors, CUSTOMER_ID)}
                />

                <div className="w-1/2">
                  <TextInput
                    register={register}
                    label={ORDER_NUM_LENGTH.label}
                    name={
                      ORDER_NUM_LENGTH.parentKey + '.' + ORDER_NUM_LENGTH.key
                    }
                    error={getNestErrorMessage(errors, ORDER_NUM_LENGTH)}
                  />
                </div>
              </div>

              <ImgUploadButton
                aggregatorID={aggregator.aggregatorId}
                confirmButtonText="Upload"
                defaultImg={aggregator?.mobile?.imgUrl}
              />
            </div>

            {/* Order Types + Keyboard Type */}
            <div className="grid grid-cols-2 gap-3">
              <div>
                {orderTypesOptions.length > 0 && (
                  <MultiSelectDropdown
                    defaultSelected={orderTypesOptions
                      .filter((ot) => ot.disabled)
                      .map((ot) => ot.value)
                      .filter((value) => value !== '')}
                    label={ORDER_TYPES.label}
                    setValue={setValue}
                    options={orderTypesOptions}
                    name={ORDER_TYPES.parentKey + '.' + ORDER_TYPES.key}
                    error={errors?.[ORDER_TYPES.key]?.message as string}
                  />
                )}

                {orderTypesOptions.some(
                  (op) => op.disabled && op.value !== '',
                ) && (
                  <div className="mt-2 flex flex-wrap gap-1.5 text-xs">
                    {orderTypesOptions
                      .filter((op) => op.disabled && op.value !== '')
                      .map((option, key) => {
                        return (
                          <span
                            className="rounded-full border border-gray-200 bg-gray-50 px-2 py-1 text-gray-700"
                            key={key}
                          >
                            {option.value}
                          </span>
                        )
                      })}
                  </div>
                )}
              </div>

              <Dropdown
                className=""
                setValue={setValue}
                defaultValue={aggregator?.mobile.keyboardType ?? false}
                options={[
                  { label: 'Numeric', value: 'numeric' },
                  { label: 'alphanumeric', value: 'alphanumeric' },
                  { label: 'Text', value: 'text' },
                ]}
                name={
                  KEYBOARD_CONFIGURATION.parentKey +
                  '.' +
                  KEYBOARD_CONFIGURATION.key
                }
                label={KEYBOARD_CONFIGURATION.label}
                error={errors?.[KEYBOARD_CONFIGURATION.key]?.message as string}
              />
            </div>
          </div>

          {/* Vertical divider (desktop only) */}
          <div className="hidden self-stretch border-r lg:block" />

          {/* Toggles */}
          <div className="flex w-full flex-col gap-4 lg:w-1/3">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="mb-1 text-sm font-semibold text-gray-900">
                Checkout Requirements
              </p>
              <div className="divide-y divide-gray-100">
                <SwitchField
                  control={control}
                  name={REQUIRE_PHONE.parentKey + '.' + REQUIRE_PHONE.key}
                  label="Require Customer Phone?"
                  defaultValue={aggregator?.mobile?.requirePhone ?? false}
                />
                <SwitchField
                  control={control}
                  name={REQUIRE_ADDRESS.parentKey + '.' + REQUIRE_ADDRESS.key}
                  label="Require Customer Address?"
                  defaultValue={aggregator?.mobile?.requireAddress ?? false}
                />
                <SwitchField
                  control={control}
                  name={
                    REQUIRE_CUSTOMER_NAME.parentKey +
                    '.' +
                    REQUIRE_CUSTOMER_NAME.key
                  }
                  label="Require Customer Name?"
                  defaultValue={
                    aggregator?.mobile?.requireCustomerName ?? false
                  }
                />
              </div>
            </div>

            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="mb-1 text-sm font-semibold text-gray-900">
                Order Entry & Picking
              </p>
              <div className="divide-y divide-gray-100">
                <SwitchField
                  control={control}
                  name={
                    IS_MANUAL_ENTRY_ENABLE.parentKey +
                    '.' +
                    IS_MANUAL_ENTRY_ENABLE.key
                  }
                  label="Is Manual Entry Enable"
                  defaultValue={aggregator?.mobile?.isManualEntryEnable ?? true}
                />
                <SwitchField
                  control={control}
                  name={
                    ENABLE_PICKER_ASSIGNMENT.parentKey +
                    '.' +
                    ENABLE_PICKER_ASSIGNMENT.key
                  }
                  label="Enable Picker Assignment on Print"
                  description="When on, the picker selected at print is assigned to a non-POS order that has no picker yet (old and new app). When off, no picker is assigned at print."
                  defaultValue={
                    aggregator?.mobile?.enablePickerAssignment ?? false
                  }
                />
                <SwitchField
                  control={control}
                  name={
                    ALLOW_ITEM_REPLACEMENT.parentKey +
                    '.' +
                    ALLOW_ITEM_REPLACEMENT.key
                  }
                  label="Allow Item Replacement"
                  description="Lets a store-app picker swap an out-of-stock item on this aggregator's orders for a cheaper barcode. Each order can be replaced once."
                  defaultValue={
                    aggregator?.mobile?.allowItemReplacement ?? false
                  }
                />
              </div>
            </div>
          </div>

          {/* Vertical divider (desktop only) */}
          <div className="hidden self-stretch border-r lg:block" />

          {/* Delivery fee + Payment Method */}
          <div className="flex w-full flex-col gap-4 lg:w-1/3">
            <div className="rounded-lg border border-gray-200 bg-white p-4">
              <p className="mb-1 text-sm font-semibold text-gray-900">
                Delivery Fee
              </p>
              <div className="divide-y divide-gray-100">
                <SwitchField
                  control={control}
                  name={
                    IS_DELIVERY_FEE_ENABLE.parentKey +
                    '.' +
                    IS_DELIVERY_FEE_ENABLE.key
                  }
                  label="Is Delivery Fee Enable"
                  defaultValue={
                    aggregator?.mobile?.isDeliveryFeeEnable ?? false
                  }
                />
                {isDeliveryFeeEnabled && (
                  <SwitchField
                    control={control}
                    name={
                      IS_DELIVERY_FEE_MANDATORY.parentKey +
                      '.' +
                      IS_DELIVERY_FEE_MANDATORY.key
                    }
                    label="Is Delivery fee mandatory"
                    defaultValue={
                      aggregator?.mobile?.isDeliveryFeeMandatory ?? false
                    }
                  />
                )}
              </div>

              {isDeliveryFeeEnabled && (
                <div className="mt-3">
                  <TextInput
                    register={register}
                    label={DELIVERY_FEE_AMOUNT.label}
                    name={
                      DELIVERY_FEE_AMOUNT.parentKey +
                      '.' +
                      DELIVERY_FEE_AMOUNT.key
                    }
                    error={getNestErrorMessage(errors, DELIVERY_FEE_AMOUNT)}
                  />
                </div>
              )}
            </div>

            <CheckBoxGroup
              required
              label="Aggregator POS Payment Modes"
              setValue={setValue}
              name={POS_PAYMENTS_MODE.parentKey + '.' + POS_PAYMENTS_MODE.key}
              options={paymentOptions}
              displayColumn={2}
              includeSelectAll
              defaultSelected={aggregator.mobile?.posPaymentMode || []}
              error={(errors as any)?.mobile?.posPaymentMode?.message as string}
            />
            {watch(POS_PAYMENTS_MODE.parentKey + '.' + POS_PAYMENTS_MODE.key)
              ?.filter((paymentId: string) =>
                ['COD', 'CCOD'].includes(paymentId?.toUpperCase()),
              )
              .map((paymentId: string) => {
                const paymentKey = paymentId?.toUpperCase()

                return (
                  <TextInput
                    key={paymentKey}
                    label={`${paymentKey} ERP Reconciliation Code`}
                    register={register}
                    name={`mobile.reconciliationPaymentTypes.${paymentKey}`}
                  />
                )
              })}
          </div>
        </div>
      </FormLayout>
    </>
  )
}

export default MobileAppConfigurationForm
